import DefaultFooter from "../components/default-footer";
import DefaultHeader from "../components/default-header";
import SectionSubscribe from "../components/sectionSubscribe";
import { useSeo } from "../components/useSeo";

const SchengenVisa = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Phase 3 note: same duplicate-content situation as schengen-visa.tsx —
  // canonical points to /visa-schengen, the primary/fullest page.
  useSeo({
    title: "Schengen Visa for Oman Residents — Eligibility & Requirements | SilverJarz",
    description: "Schengen visa information for residents of Oman: eligibility, requirements, and how SilverJarz supports your application.",
    canonical: `${origin}/visa-schengen`,
  });
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        :root {
          --primary: #294b5a;
          --primary-dark: #1a313d;
          --primary-light: #3a6275;
          --gold: #d3be76;
          --gold-light: #ede4b5;
          --gold-dark: #b8a258;
          --cream: #faf8f4;
          --text-muted: #7a8a91;
        }

        .schengen-page {
          font-family: 'DM Sans', sans-serif;
          background: var(--cream);
          color: var(--primary-dark);
        }

        /* ── HERO ── */
        .schengen-hero {
          position: relative;
          height: 420px;
          overflow: hidden;
          margin-bottom: 0;
        }

        .schengen-hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          filter: brightness(0.55);
        }

        .schengen-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            105deg,
            rgba(26, 49, 61, 0.82) 0%,
            rgba(41, 75, 90, 0.55) 50%,
            transparent 100%
          );
        }

        .schengen-hero-content {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 52px 60px;
        }

        @media (max-width: 768px) {
          .schengen-hero-content { padding: 32px 24px; }
          .schengen-hero { height: 320px; }
        }

        .schengen-hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 14px;
        }

        .schengen-hero-eyebrow::before {
          content: '';
          display: block;
          width: 24px;
          height: 1px;
          background: var(--gold);
        }

        .schengen-hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.4rem, 5vw, 3.8rem);
          font-weight: 300;
          color: #fff;
          line-height: 1.1;
          margin: 0 0 14px;
        }

        .schengen-hero-title em {
          font-style: italic;
          color: var(--gold);
        }

        .schengen-hero-desc {
          font-size: 14px;
          color: rgba(255,255,255,0.65);
          font-weight: 300;
          max-width: 480px;
          line-height: 1.7;
          margin: 0;
        }

        /* Gold accent bar at bottom of hero */
        .schengen-hero-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--gold) 0%, var(--gold-light) 50%, transparent 100%);
        }

        /* ── BREADCRUMB ── */
        .schengen-breadcrumb {
          background: #fff;
          border-bottom: 1px solid rgba(41,75,90,0.08);
          padding: 14px 0;
        }

        .schengen-breadcrumb-inner {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-muted);
          letter-spacing: 0.3px;
        }

        .schengen-breadcrumb-inner a {
          color: var(--text-muted);
          text-decoration: none;
          transition: color 0.2s;
        }

        .schengen-breadcrumb-inner a:hover { color: var(--primary); }

        .schengen-breadcrumb-sep {
          font-size: 10px;
          opacity: 0.4;
        }

        .schengen-breadcrumb-current {
          color: var(--primary);
          font-weight: 500;
        }

        /* ── MAIN LAYOUT ── */
        .schengen-main {
          padding: 60px 0 80px;
        }

        /* ── CONTENT COLUMN ── */
        .schengen-content-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.7rem, 3vw, 2.4rem);
          font-weight: 400;
          color: var(--primary-dark);
          line-height: 1.2;
          margin: 0 0 6px;
        }

        .schengen-content-title em {
          font-style: italic;
          color: var(--primary-light);
        }

        .schengen-title-underline {
          width: 48px;
          height: 2px;
          background: var(--gold);
          margin-bottom: 28px;
        }

        .schengen-lead {
          font-size: 16px;
          font-weight: 400;
          color: var(--primary-dark);
          line-height: 1.8;
          margin-bottom: 20px;
        }

        .schengen-body {
          font-size: 14.5px;
          font-weight: 300;
          color: #4a5d66;
          line-height: 1.85;
          margin-bottom: 40px;
        }

        /* ── FEATURES GRID ── */
        .schengen-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 40px;
        }

        @media (max-width: 576px) {
          .schengen-features { grid-template-columns: 1fr; }
        }

        .schengen-feature-card {
          background: #fff;
          border: 1px solid rgba(41,75,90,0.09);
          border-radius: 4px;
          padding: 20px 22px;
          display: flex;
          align-items: flex-start;
          gap: 14px;
          transition: box-shadow 0.2s, border-color 0.2s;
        }

        .schengen-feature-card:hover {
          box-shadow: 0 6px 20px rgba(41,75,90,0.1);
          border-color: rgba(211,190,118,0.35);
        }

        .schengen-feature-icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gold);
          font-size: 15px;
        }

        .schengen-feature-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--primary-dark);
          margin-bottom: 4px;
          letter-spacing: 0.2px;
        }

        .schengen-feature-text {
          font-size: 12.5px;
          color: var(--text-muted);
          line-height: 1.6;
          font-weight: 300;
        }

        /* ── CTA BLOCK ── */
        .schengen-cta-block {
          background: var(--primary);
          border-radius: 4px;
          padding: 32px 36px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
          position: relative;
          overflow: hidden;
        }

        .schengen-cta-block::before {
          content: '';
          position: absolute;
          right: -60px;
          top: -60px;
          width: 200px;
          height: 200px;
          border: 1px solid rgba(211,190,118,0.12);
          border-radius: 50%;
          pointer-events: none;
        }

        .schengen-cta-block::after {
          content: '';
          position: absolute;
          right: -30px;
          top: -30px;
          width: 130px;
          height: 130px;
          border: 1px solid rgba(211,190,118,0.08);
          border-radius: 50%;
          pointer-events: none;
        }

        .schengen-cta-text {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.5rem;
          font-weight: 400;
          color: #fff;
          margin: 0;
        }

        .schengen-cta-text em {
          font-style: italic;
          color: var(--gold);
        }

        .schengen-cta-sub {
          font-size: 13px;
          color: rgba(255,255,255,0.5);
          font-weight: 300;
          margin-top: 6px;
        }

        .schengen-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: var(--gold);
          color: var(--primary-dark);
          border: none;
          border-radius: 3px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          padding: 15px 28px;
          text-decoration: none;
          transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
          white-space: nowrap;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        .schengen-cta-btn:hover {
          background: var(--gold-light);
          box-shadow: 0 6px 20px rgba(211,190,118,0.4);
          transform: translateY(-1px);
        }

        /* ── SIDEBAR ── */
        .schengen-sidebar {
          position: sticky;
          top: 100px;
        }

        .schengen-info-card {
          background: #fff;
          border: 1px solid rgba(41,75,90,0.09);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 20px;
        }

        .schengen-info-card-header {
          background: var(--primary);
          padding: 18px 24px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .schengen-info-card-header-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(211,190,118,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gold);
          font-size: 13px;
        }

        .schengen-info-card-title {
          font-size: 13px;
          font-weight: 500;
          color: #fff;
          letter-spacing: 0.5px;
          margin: 0;
        }

        .schengen-info-list {
          padding: 20px 24px;
          list-style: none;
          margin: 0;
        }

        .schengen-info-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 0;
          border-bottom: 1px solid rgba(41,75,90,0.06);
          font-size: 13px;
          color: #4a5d66;
          font-weight: 300;
          line-height: 1.5;
        }

        .schengen-info-list li:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .schengen-info-list-icon {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(211,190,118,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          color: var(--gold-dark);
          margin-top: 1px;
        }

        .schengen-stat-card {
          background: var(--primary-dark);
          border-radius: 4px;
          padding: 28px 24px;
          margin-bottom: 20px;
        }

        .schengen-stat-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .schengen-stat-item {
          text-align: center;
        }

        .schengen-stat-num {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2rem;
          font-weight: 600;
          color: var(--gold);
          display: block;
          line-height: 1;
          margin-bottom: 4px;
        }

        .schengen-stat-lbl {
          font-size: 11px;
          color: rgba(255,255,255,0.45);
          font-weight: 300;
          letter-spacing: 0.3px;
          line-height: 1.4;
        }

        .schengen-stat-divider {
          width: 1px;
          background: rgba(255,255,255,0.08);
          margin: 0 auto;
        }

        .schengen-contact-card {
          background: var(--gold);
          border-radius: 4px;
          padding: 24px;
          text-align: center;
        }

        .schengen-contact-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.3rem;
          font-weight: 400;
          color: var(--primary-dark);
          margin-bottom: 6px;
        }

        .schengen-contact-sub {
          font-size: 12px;
          color: rgba(26,49,61,0.65);
          margin-bottom: 18px;
          font-weight: 300;
        }

        .schengen-contact-btn {
          display: block;
          background: var(--primary);
          color: #fff;
          border: none;
          border-radius: 3px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          padding: 13px 20px;
          text-decoration: none;
          text-align: center;
          transition: background 0.2s, transform 0.15s;
          width: 100%;
        }

        .schengen-contact-btn:hover {
          background: var(--primary-dark);
          transform: translateY(-1px);
        }
      `}</style>

      <div className="schengen-page">
        <div className="header-margin"></div>
        <DefaultHeader />

        {/* ── HERO ── */}
        <section className="schengen-hero">
          <img
            src="/img/pages/destinations/1.png"
            alt="Schengen visa for Oman residents"
            className="schengen-hero-img"
          />
          <div className="schengen-hero-overlay" />
          <div className="schengen-hero-content">
            <span className="schengen-hero-eyebrow">Visa Services · Oman</span>
            <h1 className="schengen-hero-title">
              Schengen Visa for<br />
              <em>Oman Residents</em>
            </h1>
            <p className="schengen-hero-desc">
              Expert visa consultation and seamless application support for residents of the Sultanate of Oman.
            </p>
          </div>
          <div className="schengen-hero-bar" />
        </section>

        {/* ── BREADCRUMB ── */}
        <div className="schengen-breadcrumb">
          <div className="container">
            <div className="schengen-breadcrumb-inner">
              <a href="/">Home</a>
              <span className="schengen-breadcrumb-sep">›</span>
              <a href="/visa">Visa Services</a>
              <span className="schengen-breadcrumb-sep">›</span>
              <span className="schengen-breadcrumb-current">Schengen Visa — Oman</span>
            </div>
          </div>
        </div>

        {/* ── MAIN ── */}
        <section className="schengen-main">
          <div className="container">
            <div className="row y-gap-40">

              {/* ── LEFT COLUMN ── */}
              <div className="col-xl-8 col-lg-7 col-12">

                <h2 className="schengen-content-title">
                  Schengen Visa for <em>Oman Residents</em>
                </h2>
                <div className="schengen-title-underline" />

                <p className="schengen-lead">
                  Applying for a Schengen visa as an Oman resident can be a detailed and time-sensitive process. Our dedicated team is here to guide you every step of the way, ensuring a smooth and successful application.
                </p>

                <p className="schengen-body">
                  At Travnook, we specialise in Schengen visa assistance for residents of the Sultanate of Oman. Whether you are based in Muscat, Salalah, or anywhere across the Sultanate, our experienced consultants help you prepare flawless documentation, schedule embassy appointments, and maximise your chances of approval on the first submission. With over a decade of expertise, we understand the specific requirements and nuances that apply to Omani residents — from income verification and travel history documentation to selecting the right embassy based on your itinerary. Apply with confidence today.
                </p>

                {/* Features */}
                <div className="schengen-features">
                  <div className="schengen-feature-card">
                    <div className="schengen-feature-icon">
                      <i className="icon-document" />
                    </div>
                    <div>
                      <div className="schengen-feature-title">Document Preparation</div>
                      <div className="schengen-feature-text">Complete checklist tailored to Omani residents with step-by-step guidance.</div>
                    </div>
                  </div>
                  <div className="schengen-feature-card">
                    <div className="schengen-feature-icon">
                      <i className="icon-calendar" />
                    </div>
                    <div>
                      <div className="schengen-feature-title">Appointment Booking</div>
                      <div className="schengen-feature-text">We secure the earliest available embassy slot on your behalf.</div>
                    </div>
                  </div>
                  <div className="schengen-feature-card">
                    <div className="schengen-feature-icon">
                      <i className="icon-shield" />
                    </div>
                    <div>
                      <div className="schengen-feature-title">High Approval Rate</div>
                      <div className="schengen-feature-text">Our meticulous review process ensures near-perfect first-submission approvals.</div>
                    </div>
                  </div>
                  <div className="schengen-feature-card">
                    <div className="schengen-feature-icon">
                      <i className="icon-support" />
                    </div>
                    <div>
                      <div className="schengen-feature-title">Dedicated Support</div>
                      <div className="schengen-feature-text">A personal consultant available throughout the process until you receive your visa.</div>
                    </div>
                  </div>
                </div>

                {/* CTA Block */}
                <div className="schengen-cta-block">
                  <div>
                    <p className="schengen-cta-text">
                      Ready to travel <em>across Europe?</em>
                    </p>
                    <p className="schengen-cta-sub">Start your Schengen application today with our experts.</p>
                  </div>
                  <a href="/apply" className="schengen-cta-btn">
                    <i className="icon-arrow-right" />
                    Apply Now
                  </a>
                </div>

              </div>

              {/* ── SIDEBAR ── */}
              <div className="col-xl-4 col-lg-5 col-12">
                <div className="schengen-sidebar">

                  {/* Stats */}
                  <div className="schengen-stat-card">
                    <div className="schengen-stat-grid">
                      <div className="schengen-stat-item">
                        <span className="schengen-stat-num">10+</span>
                        <span className="schengen-stat-lbl">Years of<br />Experience</span>
                      </div>
                      <div className="schengen-stat-item">
                        <span className="schengen-stat-num">98%</span>
                        <span className="schengen-stat-lbl">Approval<br />Rate</span>
                      </div>
                      <div className="schengen-stat-item">
                        <span className="schengen-stat-num">26</span>
                        <span className="schengen-stat-lbl">Schengen<br />Countries</span>
                      </div>
                      <div className="schengen-stat-item">
                        <span className="schengen-stat-num">48h</span>
                        <span className="schengen-stat-lbl">Avg. Processing<br />Start</span>
                      </div>
                    </div>
                  </div>

                  {/* Documents required */}
                  <div className="schengen-info-card">
                    <div className="schengen-info-card-header">
                      <div className="schengen-info-card-header-icon">
                        <i className="icon-list" />
                      </div>
                      <h3 className="schengen-info-card-title">Key Documents Required</h3>
                    </div>
                    <ul className="schengen-info-list">
                      {[
                        "Valid Omani residence permit & passport",
                        "Confirmed round-trip flight itinerary",
                        "Hotel bookings or host invitation letter",
                        "3 months bank statements",
                        "Travel insurance (min. €30,000 coverage)",
                        "Employment letter or business registration",
                        "Passport-size photos (Schengen specifications)",
                      ].map((item, i) => (
                        <li key={i}>
                          <span className="schengen-info-list-icon">✓</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Contact */}
                  <div className="schengen-contact-card">
                    <h3 className="schengen-contact-title">Need Expert Advice?</h3>
                    <p className="schengen-contact-sub">Speak with a Schengen visa consultant today — free initial consultation.</p>
                    <a href="/contact" className="schengen-contact-btn">
                      Contact a Consultant
                    </a>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
};

export default SchengenVisa;